/**
 * PatientQuotesPanel.tsx
 *
 * Patient-detail quotes section.
 * Column contract matches legacy pageID=20 (html/html/lib/tables/dt-patient-quote-list.php):
 * Created, Plan ID, Procedure(s), Effective Fee, Anesthesia, OR, Total, Balance,
 * with Plan ID only when the practice has the Plans module.
 *
 * A row opens the quote editor; Add Quote creates one and opens it straight
 * away. A quote may have no plan — legacy allows that and attaches one later
 * through Find Plan, which is why the Plan ID column is often blank.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
// formatDate parses a bare YYYY-MM-DD as LOCAL midnight (via parseDateValue),
// which is what these legacy date-only columns need — parsed as UTC they render
// a day early west of Greenwich — and it also maps the legacy '0000-00-00'
// zero sentinel to a dash.
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable';
import { getErrorMessage } from '@/services/apiClient';
import { createQuote, getQuotesByPatient, type PatientQuoteRow } from '@/services/quotes.service';
import { useModuleAccess } from '@/hooks/useAccountModules';
import { PLANS_MODULE_KEY } from '@/pages/patientDetailSections';
import QuoteEditorModal from './QuoteEditorModal';

/**
 * A column heading in the legacy list's style. DataTable lays its heading out
 * as a flex row, which ignores the cell's text-align, so `right` pushes a
 * right-aligned heading over with an auto margin instead.
 */
function Heading({ children, right = false }: { children: string; right?: boolean }) {
  return (
    <span className={cn('font-medium uppercase tracking-wide text-slate-500', right && 'ml-auto')}>
      {children}
    </span>
  );
}

function money(
  key: keyof PatientQuoteRow,
  label: string,
  value: (quote: PatientQuoteRow) => number | null,
): DataTableColumn<PatientQuoteRow> {
  return {
    key,
    header: <Heading right>{label}</Heading>,
    label,
    role: 'field',
    align: 'right',
    render: (quote) => {
      const amount = value(quote);
      // Blank rather than $0.00, so a card leaves the line out.
      return amount === null ? '' : <span className="whitespace-nowrap">{formatCurrency(amount)}</span>;
    },
  };
}

interface PatientQuotesPanelProps {
  patientId: number;
  patientName?: string;
}

export default function PatientQuotesPanel({ patientId, patientName = '' }: PatientQuotesPanelProps) {
  const queryClient = useQueryClient();
  const [openQuoteId, setOpenQuoteId] = useState<number | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  // Legacy adds the Plan ID column only when the Plans module is on
  // (lib/tables/dt-patient-quote-list.php:177-179).
  const { enabled: plansEnabled } = useModuleAccess(PLANS_MODULE_KEY);

  const { data: quotes, isLoading, isError } = useQuery({
    queryKey: ['patient-quotes', patientId],
    queryFn: () => getQuotesByPatient(patientId),
    staleTime: 30_000,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['patient-quotes', patientId] });
  };

  const addMutation = useMutation({
    // No plan: a quote started from the chart is attached to one later.
    mutationFn: () => createQuote({ patientId }),
    onSuccess: (quote) => {
      setAddError(null);
      refresh();
      setOpenQuoteId(quote.quoteId);
    },
    onError: (err) => setAddError(getErrorMessage(err, 'Could not start a new quote. Try again. If it keeps happening, contact Prospera support.')),
  });

  const columns: DataTableColumn<PatientQuoteRow>[] = [
    {
      key: 'created',
      header: <Heading>Created</Heading>,
      label: 'Created',
      role: 'title',
      // The row is clickable too, but only this reaches a keyboard or a
      // screen reader.
      render: (quote) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setOpenQuoteId(quote.quoteId);
          }}
          aria-label={`Open quote from ${formatDate(quote.created)}`}
          className="whitespace-nowrap rounded text-left hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          {formatDate(quote.created)}
        </button>
      ),
    },
    ...(plansEnabled
      ? [{
          key: 'planDisplayId',
          header: <Heading>Plan ID</Heading>,
          label: 'Plan ID',
          role: 'field' as const,
          render: (quote: PatientQuoteRow) => <span className="whitespace-nowrap">{quote.planDisplayId ?? '—'}</span>,
        }]
      : []),
    {
      key: 'procedures',
      header: <Heading>Procedure(s)</Heading>,
      label: 'Procedure(s)',
      role: 'subtitle',
      width: 'max-w-[280px]',
      truncate: true,
      render: (quote) => (quote.procedures.length > 0 ? quote.procedures.join(', ') : '—'),
    },
    money('effectiveFee', 'Effective Fee', (quote) => quote.effectiveFee),
    money('anesthesia', 'Anesthesia', (quote) => quote.anesthesia),
    money('operatingRoom', 'OR', (quote) => quote.operatingRoom),
    money('total', 'Total', (quote) => quote.total),
    // Legacy shows a balance only on the active quote.
    money('balance', 'Balance', (quote) => quote.balance),
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between max-md:flex-wrap max-md:gap-2">
        <h2 className="text-lg font-semibold text-slate-900">Quotes</h2>
        <Button
          onClick={() => addMutation.mutate()}
          loading={addMutation.isPending}
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
        >
          Add Quote
        </Button>
      </div>

      {addError ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{addError}</div>
      ) : null}

      {isLoading && <p className="text-sm text-slate-400">Loading quotes…</p>}

      {isError && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          Could not load this patient&rsquo;s quotes. Refresh the page, and if it keeps
          happening, contact Prospera support.
        </div>
      )}

      {!isLoading && !isError && quotes?.length === 0 && (
        <p className="py-4 text-sm text-slate-400 italic">No quotes yet.</p>
      )}

      {quotes && quotes.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <DataTable<PatientQuoteRow>
            data={quotes}
            columns={columns}
            keyField="quoteId"
            onRowClick={(quote) => setOpenQuoteId(quote.quoteId)}
            // Legacy marks the active quote bold and orange (#c94302). The
            // colour goes on the cells because DataTable gives each cell its
            // own text colour, which would win over one set on the row.
            rowClassName={(quote) =>
              quote.activeQuote ? 'bg-orange-50/40 font-semibold [&>td]:text-[#c94302]' : undefined
            }
            className="border-t-0"
          />
        </div>
      )}

      <QuoteEditorModal
        quoteId={openQuoteId}
        patientName={patientName}
        onClose={() => setOpenQuoteId(null)}
        onChanged={refresh}
        onOpenQuote={setOpenQuoteId}
      />
    </div>
  );
}
